import { AsyncPipe } from "@angular/common";
import { Component, computed, effect, inject, input, output } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildCentralStationGroupedSourceItems,
  CentralStationGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getCentralStationGroupedPartLabels,
  getMonitoringConfigurationSummaryTitle,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { shareReplay, switchMap } from "rxjs";

@Component({
  selector: "app-central-station-notes",
  imports: [
    FloorplanItemDetailsComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
    FloorplanCustomPartNumbersComponent,
    AsyncPipe,
  ],
  templateUrl: "./central-station-notes.component.html",
  styleUrl: "./central-station-notes.component.scss",
  standalone: true,
})
export class CentralStationNotesComponent {
  private readonly monitoringProductImageService = inject(MonitoringProductImageService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();
  protected readonly centralStation = computed(() => this.floorplanItem() as FloorplanCentralStation);
  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  protected productItems!: MonitoringProductItem[];
  protected monitoringItems: MonitoringGroupedPartSourceItem[] = [];
  protected readonly fallbackImageUrl = "./assets/floorplan-menu-icons/central_station_menu.svg";
  protected monitoringTitle = getMonitoringConfigurationSummaryTitle(MonitoringCategory.CENTRAL_STATIONS);
  private readonly categoryLabels: CentralStationGroupedPartLabels = getCentralStationGroupedPartLabels();
  protected readonly imageUrl$ = toObservable(this.centralStation).pipe(
    switchMap((centralStation) => this.monitoringProductImageService.resolveCentralStationImageUrl(centralStation.config)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    effect(() => {
      const centralStation = this.centralStation();
      this.productItems = centralStation.config.getOptions();
      this.monitoringItems = this.buildMonitoringItems(centralStation.config);
    });
  }

  private buildMonitoringItems(config: CentralStationConfiguration): MonitoringGroupedPartSourceItem[] {
    return buildCentralStationGroupedSourceItems(
      {
        baseOptions: config.baseOptions,
        licence: config.licence,
        additionalLicences: config.additionalLicences,
        accessories: config.accessories,
      },
      this.categoryLabels,
    );
  }
}
